import type { PropsWithChildren } from 'react'

import { Helmet } from 'react-helmet-async'
import { ThemeProvider } from 'styled-components'

import { AppContainer, AppFrame, AppViewport } from './styles'

import { Background } from 'components/Background'
import { Loader } from 'components/Loader'
import { Logo } from 'components/Logo'
import { SyncServerContextProvider } from 'context/SyncServerContext'
import { useEsConfig } from 'hooks/useEsConfig'
import { useEventLock } from 'hooks/useEventLock'
import { useStylesheet } from 'hooks/useStylesheet'
import { GlobalStyle } from 'theme/global'
import { useSettings } from 'utils/settings'
import { Locked } from 'views/errors/Locked'

/**
 * The root layout for the app. This component should only be used once.
 */
export const Layout = ({ children }: PropsWithChildren): JSX.Element => {
  const esConfig = useEsConfig()
  const { getThemeForEntity } = useStylesheet()
  const { getTranslation } = useSettings()
  const theme = getThemeForEntity(esConfig)
  const isLocked = useEventLock()

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <meta name="theme-color" content={theme.color.surface2} />
        <meta name="color-scheme" content={theme.mode} />
        <title>{getTranslation(esConfig?.name)}</title>
      </Helmet>
      <GlobalStyle theme={theme} />
      <Loader>
        <AppFrame>
          <Background animated />
          <AppViewport>
            <SyncServerContextProvider>
              <AppContainer>
                {isLocked ? (
                  <Locked />
                ) : (
                  <>
                    <Logo />
                    {children}
                  </>
                )}
              </AppContainer>
            </SyncServerContextProvider>
          </AppViewport>
        </AppFrame>
      </Loader>
    </ThemeProvider>
  )
}
